import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useLocalStorage } from 'usehooks-ts';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import SupportiveMessage, {
  SupportiveMessageActionsPosition,
  SupportiveMessageColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import Typography from '@lib/components/Typography/Typography';
import { AI_INDEXING_PARKED_BANNER } from '@lib/enums/localStorageKeys';
import { IndexingStatus } from '@lib/utils/indexingStateCopy';
import getParkedReasonLines, {
  IndexingFailureReason,
} from '@lib/utils/indexingFailureReasonCopy';
import styles from './IndexingStatusMessages.module.scss';

export interface IndexingStatusMessagesProps {
  // The caller's placement for the block — the component renders nothing at all
  // when there is nothing to say, so an empty wrapper never reserves a row.
  className?: string;
  // Scopes the dismissal. The system app shows a different company's card on
  // every account page, and dismissing one must not silence the rest.
  storageId: string;
  status: IndexingStatus | null;
  parkedCount?: number | null;
  parkedReasons?: readonly IndexingFailureReason[] | null;
  // Success or failure, so it advances whenever indexing runs at all — which is
  // what tells a dismissal that its run is over. See getParkedSignature.
  lastIndexingAttemptAt?: string | null;
  onRetryParked: () => void;
  retryParkedLoading?: boolean;
  // Opens the list behind the count. Optional because only one caller can offer
  // it: the documents come from `documentations`, which is scoped to the signed
  // -in company, so on the system app's card — showing ANOTHER tenant — the
  // same link would open the operator's own documents. Omitted there, and the
  // headline stays plain text.
  onParkedClick?: () => void;
}

/**
 * Identifies the run whose verdict a dismissal was about.
 *
 * There is no id for an indexing run to key this on: the backend has no run
 * record at all — `ReindexEmbeddingsWorker` fans jobs out and exits, indexing is
 * a continuous lane rather than a job, and `AiIndexingCoverage` carries no
 * identifier. Two things stand in for one, and both are needed:
 *
 * - **the parked set** (count + reasons) — catches a verdict that changes while
 *   the reader is looking, without anything having to be re-attempted.
 * - **`lastIndexingAttemptAt`** — the run discriminator. Parked documents are
 *   never retried automatically, so this timestamp is still exactly when the
 *   backlog was last worked on; the moment any run touches it, the dismissal
 *   stops applying. Crucially this needs no luck: the set alone would silence
 *   every later run that parks the same documents, and the alternative — noticing
 *   `parked` pass through zero — only works if this card is mounted at that
 *   instant, which it usually is not, since dismissing it also hides the Retry
 *   that would have started the run.
 */
export function getParkedSignature(
  parkedCount?: number | null,
  parkedReasons?: readonly IndexingFailureReason[] | null,
  lastIndexingAttemptAt?: string | null,
): string {
  const reasons = (parkedReasons || [])
    .map(({ reason, count }) => `${reason}=${count}`)
    // Server order is "commonest first", so it shifts as counts move even when
    // the set does not — sort to compare sets, not orderings.
    .sort()
    .join(',');
  return `${parkedCount ?? 0}|${reasons}@${lastIndexingAttemptAt ?? ''}`;
}

/**
 * What the indexing row cannot say on its own, as banners underneath it.
 *
 * Only two things qualify: a brake or a fault holding the backlog
 * (`indexingState`), and documents nothing will ever retry (`parked`). Ordinary
 * progress is not among them — the bar, the spinning label icon and the ETA
 * beside the bar already carry it, and a banner repeating them is a fourth
 * voice saying the same thing.
 */
function IndexingStatusMessages({
  className,
  storageId,
  status,
  parkedCount,
  parkedReasons,
  lastIndexingAttemptAt,
  onRetryParked,
  retryParkedLoading,
  onParkedClick,
}: IndexingStatusMessagesProps) {
  const { t } = useTranslation();
  // Survives a reload, and only a reload: logout clears localStorage wholesale,
  // so the next session is told about the parked documents again even though
  // nothing about them changed.
  const [dismissed, setDismissed] = useLocalStorage<Record<string, string>>(
    AI_INDEXING_PARKED_BANNER,
    {},
  );

  // Nothing is parked, so there is no verdict left to have an opinion about —
  // drop the record rather than leave the map growing with entries for companies
  // whose backlog was cleared long ago. Not the mechanism that ends a dismissal:
  // that is the run stamp inside the signature, which needs no one watching.
  const forgetDismissal = useCallback(() => {
    setDismissed((prev) => {
      if (!(storageId in prev)) return prev;
      const next = { ...prev };
      delete next[storageId];
      return next;
    });
  }, [setDismissed, storageId]);

  // `parked` is a non-null Int, so a missing count is never "nothing is parked"
  // — it is a card whose coverage has not arrived yet. Treating the two alike
  // dropped the record on the first render of every mount that renders while
  // the query is still in flight (the tenant dashboard does; the system app
  // holds the whole panel behind a loader), which is why a dismissal there
  // lasted only until reload.
  useEffect(() => {
    if (parkedCount === 0) forgetDismissal();
  }, [parkedCount, forgetDismissal]);

  const parkedReasonLines = getParkedReasonLines(parkedReasons, t);
  const parkedSignature = getParkedSignature(
    parkedCount,
    parkedReasons,
    lastIndexingAttemptAt,
  );
  const showsStatus = !!status?.showsMessage;
  // The retry is in flight, so what is on screen describes the run being
  // replaced: hide it the moment re-indexing starts and let the response say
  // what the new run found — `parked: 0` on success, so it stays away until
  // documents are given up on again. A retry that FAILS restores the banner,
  // since loading ends with the same parked set still reported.
  const showsParked =
    !!parkedCount &&
    !retryParkedLoading &&
    dismissed[storageId] !== parkedSignature;

  if (!showsStatus && !showsParked) return null;

  return (
    <div className={classNames(styles.messages, className)}>
      {showsStatus && status && (
        <SupportiveMessage
          color={status.color}
          leftIcon={status.icon}
          leftIconSymbolsOutlined
          fullWidth
          hasBottomSpace={false}
          text={status.label}
        />
      )}
      {showsParked && (
        <SupportiveMessage
          color={SupportiveMessageColor.Red}
          fullWidth
          hasBottomSpace={false}
          actionsPosition={SupportiveMessageActionsPosition.FlexStart}
          text={
            <>
              {/* The count leads to the documents it counts, when the caller
                  can reach them. Styled to inherit the banner's own colour
                  rather than as a Link, which would force its blue into a red
                  message; the underline is what marks it clickable. */}
              <Typography
                variant="label"
                block
                component={onParkedClick ? 'button' : undefined}
                className={
                  onParkedClick ? styles.parkedHeadlineButton : undefined
                }
                data-test-id="indexing-parked__count-button"
                onClick={onParkedClick}
              >
                {t('ai-indexing-state-blocked', { count: parkedCount })}
              </Typography>
              {/* Why they gave up, so the number leads to an action: an
                  unreadable file needs replacing, a missing one re-uploading —
                  neither of which the retry beside it fixes on its own. */}
              {parkedReasonLines.map(({ key, label }) => (
                <Typography key={key} variant="caption" block>
                  {label}
                </Typography>
              ))}
            </>
          }
          actions={
            <>
              <Button
                buttonType={ButtonTypes.primaryText}
                buttonSize={ButtonSizes.small}
                buttonText={t('dismiss')}
                data-test-id="indexing-parked__dismiss-button"
                onClick={() =>
                  setDismissed((prev) => ({
                    ...prev,
                    [storageId]: parkedSignature,
                  }))
                }
              />
              <Button
                buttonType={ButtonTypes.primaryFilled}
                buttonSize={ButtonSizes.small}
                buttonText={t('ai-retry-parked')}
                leftIcon="restart_alt"
                isLoading={retryParkedLoading}
                disabled={retryParkedLoading}
                // Called with no arguments on purpose: both callers pass an
                // Apollo mutate function straight in, and `Button` invokes
                // `onClick(event)` — which would hand the click event to the
                // mutation as its options object.
                onClick={() => onRetryParked()}
              />
            </>
          }
        />
      )}
    </div>
  );
}

export default IndexingStatusMessages;
